import {
  ComposedChart, Bar, Line, ReferenceLine, ReferenceArea, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, LabelList,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthDay = (md) => {
  const [m, d] = String(md || "").split("/");
  return m && d ? [MONTHS[Number(m) - 1] || m, d.padStart(2, "0")] : ["", ""];
};

// X tick: opponent logo (falls back to vs/@ABBR) + "Nov / 17" date, like the reference style.
const LogoTick = ({ x, y, payload, data }) => {
  const d = data[payload?.index] || {};
  const dense = data.length > 12;
  const s = dense ? 12 : 16;
  const [mon, day] = monthDay(d.date);
  return (
    <g transform={`translate(${x},${y})`}>
      {d.logo ? (
        <>
          <circle cx={0} cy={2 + s / 2} r={s / 2 + 2} fill="#E2E8F0" fillOpacity={0.9} />
          <image href={d.logo} x={-s / 2 + 1} y={3} width={s - 2} height={s - 2} />
        </>
      ) : (
        <text y={12} textAnchor="middle" fill="#94A3B8" fontSize={7} fontFamily="monospace">
          {d.home ? "vs" : "@"}{d.opp}
        </text>
      )}
      <text y={s + 12} textAnchor="middle" fill="#CBD5E1" fontSize={dense ? 6.5 : 8} fontFamily="monospace">{mon}</text>
      <text y={s + 21} textAnchor="middle" fill="#CBD5E1" fontSize={dense ? 6.5 : 8} fontFamily="monospace">{day}</text>
    </g>
  );
};

// Real value printed at the bottom of each bar ("?" for the upcoming game).
const BottomLabel = ({ x, y, width, height, value, index, data }) => {
  const d = data[index] || {};
  const cx = x + width / 2;
  const fs = width < 14 ? 7.5 : width < 22 ? 9 : 11;
  if (d.upcoming) {
    return <text x={cx} y={y + height / 2 + 4} textAnchor="middle" fill="#94A3B8" fontSize={fs + 2} fontWeight={700}>?</text>;
  }
  if (value == null) return null;
  const label = Number.isInteger(value) ? value : value.toFixed(1);
  const inside = height >= fs + 6;
  return (
    <text x={cx} y={inside ? y + height - 5 : y - 3} textAnchor="middle"
      fill={inside ? "#FFFFFF" : "#94A3B8"} fontSize={fs} fontWeight={700} fontFamily="monospace">
      {label}
    </text>
  );
};

const LinePill = ({ viewBox, value }) => {
  if (!viewBox) return null;
  const w = 30;
  return (
    <g>
      <rect x={viewBox.x - w - 2} y={viewBox.y - 8} width={w} height={16} rx={3} fill="#3F3A1A" stroke="#FACC15" strokeWidth={0.5} />
      <text x={viewBox.x - w / 2 - 2} y={viewBox.y + 4} textAnchor="middle" fill="#FACC15" fontSize={9} fontWeight={700} fontFamily="monospace">
        {value}
      </text>
    </g>
  );
};

const FIB_LEVELS = [
  [1, "#94A3B8"], [0.786, "#22D3EE"], [0.618, "#14B8A6"], [0.5, "#22C55E"], [0.382, "#F59E0B"], [0.236, "#EF4444"], [0, "#94A3B8"],
];

// Fibonacci retracement from the window's highest to lowest real game.
const fibFrom = (rows) => {
  const real = rows.filter((d) => !d.upcoming && d.v != null);
  if (real.length < 2) return null;
  const hi = real.reduce((a, d) => (d.v > a.v ? d : a));
  const lo = real.reduce((a, d) => (d.v < a.v ? d : a));
  if (hi.v === lo.v) return null;
  return { hi, lo, levels: FIB_LEVELS.map(([lvl, c]) => ({ lvl, c, y: Math.round((lo.v + (hi.v - lo.v) * lvl) * 10) / 10 })) };
};

const LineDot = ({ cx, cy, payload, line }) => {
  if (cx == null || cy == null || payload?.upcoming || payload?.v == null) return <g />;
  return <circle cx={cx} cy={cy} r={3} fill={payload.v > line ? "#10B981" : "#EF4444"} stroke="#0B0F17" strokeWidth={1} />;
};

const LineValue = ({ x, y, value, index, data }) => {
  if (value == null || data[index]?.upcoming) return null;
  return (
    <text x={x} y={y - 7} textAnchor="middle" fill="#E2E8F0" fontSize={8.5} fontWeight={700} fontFamily="monospace">
      {Number.isInteger(value) ? value : value.toFixed(1)}
    </text>
  );
};

// MA line ends on the next game with its projected value.
const MaDot = ({ cx, cy, payload }) => {
  if (!payload?.upcoming || cx == null || cy == null) return <g />;
  return (
    <g>
      <circle cx={cx} cy={cy} r={4.5} fill="#F43F5E" stroke="#fff" strokeWidth={1} />
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#F43F5E" fontSize={10} fontWeight={800} fontFamily="monospace">
        {payload.ma}
      </text>
    </g>
  );
};

export const GameLogChart = ({ prop, rows, nextGame, metricLabel, view = { type: "bar" }, maNext = null, fullscreen = false }) => {
  const isLine = view.type === "line";
  const data = rows.map((r, i) => ({ ...r, key: String(i) }));
  if (nextGame) {
    const dt = new Date(nextGame.date);
    data.push({
      key: "next", upcoming: true, v: isLine ? null : prop.line, ma: maNext ?? undefined,
      opp: nextGame.opp, home: nextGame.home, logo: nextGame.logo,
      date: `${dt.getMonth() + 1}/${dt.getDate()}`,
    });
  }
  const fib = view.fib ? fibFrom(data) : null;
  const lastIdx = data.filter((d) => d.season === "last").map((d) => d.key);
  const curIdx = data.filter((d) => d.season === "cur" || d.upcoming).map((d) => d.key);
  const split = lastIdx.length > 0 && curIdx.length > 0;

  return (
    <div className={`${fullscreen ? "h-[max(360px,calc(100vh-330px))]" : "h-56"} w-full [&_*:focus]:outline-none`} data-testid={`gamelog-chart-${prop.id}`} data-chart-type={view.type}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 22, right: fib ? 66 : view.ma ? 22 : 8, left: 34, bottom: 0 }} barCategoryGap="12%">
          <XAxis dataKey="key" axisLine={false} tickLine={false} interval={0} height={data.length > 12 ? 44 : 50}
            tick={<LogoTick data={data} />} />
          <YAxis hide domain={[0, (max) => Math.max(max, prop.line || 0, maNext || 0) * 1.15 || 1]} />
          {split && (
            <ReferenceArea x1={lastIdx[0]} x2={lastIdx[lastIdx.length - 1]} fill="#FFFFFF" fillOpacity={0.05}
              label={{ value: "Last season", position: "insideTopLeft", fill: "#94A3B8", fontSize: 9, dy: -18 }} />
          )}
          {split && (
            <ReferenceArea x1={curIdx[0]} x2={curIdx[curIdx.length - 1]} fillOpacity={0}
              label={{ value: "This season", position: "insideTopLeft", fill: "#94A3B8", fontSize: 9, dy: -18 }} />
          )}
          <Tooltip
            cursor={{ fill: "rgba(255,255,255,0.04)" }}
            contentStyle={{ background: "#0B0F17", border: "1px solid #233248", borderRadius: 8, fontSize: 12 }}
            formatter={(val, name, item) => {
              if (name === "v") return [item.payload.upcoming ? "Upcoming" : val, metricLabel];
              if (name === "ma" && val != null) return [val, item.payload.upcoming ? `MA${view.ma} projection` : `MA${view.ma}`];
              return null;
            }}
            labelFormatter={(_, payload) => {
              const d = payload && payload[0] && payload[0].payload;
              return d ? `${d.home ? "vs" : "@"}${d.opp} · ${d.date}` : "";
            }}
          />
          {fib && fib.levels.map((l) => (
            <ReferenceLine key={l.lvl} y={l.y} stroke={l.c} strokeOpacity={0.8} strokeWidth={1}
              label={{ value: `${l.lvl} (${l.y})`, position: "right", fill: l.c, fontSize: 8.5, fontFamily: "monospace" }} />
          ))}
          {fib && (
            <ReferenceLine stroke="#CBD5E1" strokeDasharray="4 4" strokeOpacity={0.7}
              segment={[{ x: fib.hi.key, y: fib.hi.v }, { x: fib.lo.key, y: fib.lo.v }]} />
          )}
          {isLine ? (
            <Line type="linear" dataKey="v" stroke="#3B82F6" strokeWidth={2} isAnimationActive={false}
              dot={<LineDot line={prop.line} />} activeDot={{ r: 4 }}>
              <LabelList dataKey="v" content={(p) => <LineValue {...p} data={data} />} />
            </Line>
          ) : (
            <Bar dataKey="v" radius={[3, 3, 0, 0]} isAnimationActive={false} minPointSize={2}>
              {data.map((d) => (
                <Cell key={d.key}
                  fill={d.upcoming ? "transparent" : d.v > prop.line ? "#10B981" : "#EF4444"}
                  fillOpacity={fib ? 0.75 : 1}
                  stroke={d.upcoming ? "#94A3B8" : undefined}
                  strokeDasharray={d.upcoming ? "4 3" : undefined} />
              ))}
              <LabelList dataKey="v" content={(p) => <BottomLabel {...p} data={data} />} />
            </Bar>
          )}
          {isLine && nextGame && (
            <ReferenceLine x="next" stroke="#94A3B8" strokeDasharray="4 3"
              label={{ value: "?", position: "insideBottom", fill: "#94A3B8", fontSize: 12, fontWeight: 700 }} />
          )}
          <ReferenceLine y={prop.line} stroke="#FACC15" strokeDasharray="5 4" label={<LinePill value={prop.line} />} />
          {view.trend !== false && (
            <Line type="monotone" dataKey="t" stroke="#FB923C" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          )}
          {view.ma && (
            <Line type="monotone" dataKey="ma" stroke="#F43F5E" strokeWidth={2} connectNulls isAnimationActive={false}
              dot={<MaDot />} activeDot={false} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
